import React from "react";
import * as Icon from "react-feather";
import Modal from "react-responsive-modal";
import AFSService from "../../../../../services/AFSService";
import WComponent from "../../../../common/WComponent";
import "./document-viewer-modal.css";

/**
 * Inline document viewer.
 *
 * Renders a source document (PDF, image, etc.) inside a react-responsive-modal
 * via an <iframe> pointing at /tools/afs/document_view. The backend streams
 * the file with Content-Disposition: inline so browsers render natively
 * instead of downloading.
 *
 * Props:
 *   open               — controls visibility
 *   onClose            — called when the user dismisses
 *   doc                — { id?, name, doc_type, source? } — the document
 *                        descriptor used elsewhere in the app. We resolve to
 *                        evidenceItemId / documentId based on `source`.
 *   evidenceItemId     — explicit override (preferred when source === "evidence_item")
 *   documentId         — explicit override (preferred when source === "document")
 */
class DocumentViewerModal extends WComponent {
	state = { errored: false };

	// Reset error state whenever the doc changes (mirrors the old useEffect deps).
	componentDidUpdate(prevProps) {
		let prevDocId = prevProps.doc && prevProps.doc.id;
		let docId = this.props.doc && this.props.doc.id;
		if (
			prevProps.open !== this.props.open ||
			prevProps.evidenceItemId !== this.props.evidenceItemId ||
			prevProps.documentId !== this.props.documentId ||
			prevDocId !== docId
		) {
			if (this.state.errored) this.update({ errored: false });
		}
	}

	render() {
		let { open, onClose, doc, evidenceItemId, documentId } = this.props;
		let { errored } = this.state;

		// Resolve which id to send. Explicit overrides win; otherwise infer from doc.source.
		let resolvedEvId = evidenceItemId;
		let resolvedDocId = documentId;
		if (resolvedEvId == null && resolvedDocId == null && doc) {
			if (doc.source === "document") resolvedDocId = doc.id;
			else resolvedEvId = doc.id; // default — agent-mode docs are usually evidence items
		}

		let url = (resolvedEvId != null || resolvedDocId != null)
			? AFSService.getDocumentViewUrl({ evidenceItemId: resolvedEvId, documentId: resolvedDocId })
			: null;

		let title = (doc && (doc.name || doc.doc_type)) || "Document";

		return (
			<Modal
				open={open}
				center
				onClose={onClose}
				showCloseIcon={false}
				classNames={{ modal: "document-viewer-modal", modalContainer: "document-viewer-modal-container" }}
				styles={{ modal: { width: "85vw", maxWidth: 1100, height: "90vh", padding: 0, display: "flex", flexDirection: "column", borderRadius: 6, overflow: "hidden" } }}
			>
				<div className="document-viewer-modal__header">
					<Icon.FileText size={16} color="#395395" className="document-viewer-modal__header-icon" />
					<span className="document-viewer-modal__title">{title}</span>
					{url && (
						<a
							href={url}
							target="_blank"
							rel="noopener noreferrer"
							title="Open in a new tab"
							className="document-viewer-modal__open-link"
						>
							<Icon.ExternalLink size={11} />
							<span>Open in new tab</span>
						</a>
					)}
					<button
						type="button"
						onClick={onClose}
						title="Close"
						aria-label="Close"
						className="document-viewer-modal__close-btn"
					>
						<Icon.X size={14} />
					</button>
				</div>
				<div className="document-viewer-modal__body">
					{!url && (
						<div className="document-viewer-modal__empty">
							No source available for this document.
						</div>
					)}
					{url && !errored && (
						<iframe
							title={title}
							src={url}
							className="document-viewer-modal__iframe"
							onError={() => this.update({ errored: true })}
						/>
					)}
					{url && errored && (
						<div className="document-viewer-modal__error">
							<Icon.AlertCircle size={28} color="#e6a117" />
							<div>Couldn't load the document inline.</div>
							<a href={url} target="_blank" rel="noopener noreferrer" className="document-viewer-modal__error-link">Open in a new tab</a>
						</div>
					)}
				</div>
			</Modal>
		);
	}
}

export default DocumentViewerModal;
